var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useKeyboard } from '@react-native-community/hooks';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useDevTool } from '@/components/DevTool/useDevTool';
import { getCenterAndRadius } from '@/components/maps/Types.tsx';
import { PlaceSearchResultModeDto, SearchPlaceSortDto, } from '@/generated-sources/openapi';
import useAppComponents from '@/hooks/useAppComponents';
import { useLogger } from '@/logging/useLogger';
import { SortOption, draftCameraRegionAtom, filterAtom, pinnedPlaceAtom, searchModeAtom, searchQueryAtom, searchRequestIdAtom, viewStateAtom, } from '@/screens/SearchScreen/atoms';
import { mapSummaryToToiletDetails, } from '@/components/toilet/data';
import { useUpdateSearchQuery } from '@/screens/SearchScreen/useUpdateSearchQuery.tsx';
import GeolocationUtils from '@/utils/GeolocationUtils';
import ToastUtils from '@/utils/ToastUtils.ts';
function generateRequestId() {
    const now = new Date();
    const pad = (n, len = 2) => String(n).padStart(len, '0');
    const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}${pad(now.getMilliseconds(), 3)}`;
    return `${timestamp}_${Math.random().toString(36).slice(2, 8)}`;
}
export default function useSearchRequest() {
    var _a, _b, _c;
    const { api, toiletApi } = useAppComponents();
    const { sortOption, scoreUnder, hasSlope, isRegistered, hasReview } = useAtomValue(filterAtom);
    const { text, location, radiusMeter, useCameraRegion } = useAtomValue(searchQueryAtom);
    const searchMode = useAtomValue(searchModeAtom);
    const pinnedPlace = useAtomValue(pinnedPlaceAtom);
    const draftCameraRegion = useAtomValue(draftCameraRegionAtom);
    const viewState = useAtomValue(viewStateAtom);
    const setSearchRequestId = useSetAtom(searchRequestIdAtom);
    const setSearchQuery = useSetAtom(searchQueryAtom);
    const logger = useLogger();
    const devTool = useDevTool();
    const keyboard = useKeyboard();
    const keyboardRef = useRef(keyboard);
    const { data, isFetching, refetch } = useQuery({
        initialData: { mode: 'place', items: [] },
        throwOnError: error => {
            ToastUtils.showOnApiError(error);
            return false;
        },
        queryKey: [
            'search',
            {
                text,
                location,
                sortOption,
                scoreUnder,
                hasSlope,
                isRegistered,
                hasReview,
                useCameraRegion,
                searchMode,
                pinnedPlaceId: (_a = pinnedPlace === null || pinnedPlace === void 0 ? void 0 : pinnedPlace.place.id) !== null && _a !== void 0 ? _a : null,
            },
        ],
        queryFn: (_a) => __awaiter(this, [_a], void 0, function* ({ signal }) {
            var _b, _c, _d, _e, _f, _g, _h;
            if (!text) {
                return null; // No search text -> Do not call API because it is landing page
            }
            // 자동완성에서 고른 장소는 그 1건만 보여준다. 서버에 다시 묻지 않는다 —
            // 장소명으로 재검색하면 동명/유사명 장소가 섞여 "고른 그곳"이 아닌 목록이 된다.
            // 검색 이벤트도 남기지 않는다: 서버 검색이 일어나지 않았고, 항목 클릭 자체는
            // SearchItemSummary 가 이미 로깅한다(직전 검색의 search_request_id 로 조인된다).
            if (pinnedPlace) {
                const pinnedResult = [pinnedPlace];
                (_b = onFetchCompleted.current) === null || _b === void 0 ? void 0 : _b.call(onFetchCompleted, pinnedResult);
                onFetchCompleted.current = () => { };
                return { mode: 'place', items: pinnedResult };
            }
            let sort;
            let currentLocation;
            if (sortOption === SortOption.DISTANCE) {
                sort = SearchPlaceSortDto.Distance;
            }
            else if (sortOption === SortOption.LOW_SCORE) {
                sort = SearchPlaceSortDto.AccessibilityScore;
            }
            else {
                sort = SearchPlaceSortDto.Accuracy;
            }
            if (location === null) {
                const currentPosition = yield GeolocationUtils.getCurrentPosition();
                currentLocation = {
                    lat: currentPosition.coords.latitude,
                    lng: currentPosition.coords.longitude,
                };
            }
            // Track the search request in DevTool
            let searchLocation = location !== null && location !== void 0 ? location : currentLocation;
            let searchRadius = radiusMeter !== null && radiusMeter !== void 0 ? radiusMeter : 20000;
            let rectangleRegion;
            // 지도 영역(rectangle)으로 검색하는 것은 **지도 화면을 보면서 검색한 경우**뿐이다
            // (useCameraRegion: "이 지역 재검색", 지도 모드 카테고리 칩).
            // draftCameraRegion 이 남아있다는 이유만으로 고정하면, 검색어 입력 화면에서 한 검색도
            // 직전에 보던 지도 영역 안에서만 찾게 된다.
            if (draftCameraRegion && useCameraRegion) {
                rectangleRegion = {
                    leftTopLocation: {
                        lat: draftCameraRegion.northEast.latitude,
                        lng: draftCameraRegion.southWest.longitude,
                    },
                    rightBottomLocation: {
                        lat: draftCameraRegion.southWest.latitude,
                        lng: draftCameraRegion.northEast.longitude,
                    },
                };
                // For DevTool tracking, use rectangle tracking
                devTool.searchRegion.trackRectangle(rectangleRegion.leftTopLocation, rectangleRegion.rightBottomLocation);
                // For fallback compatibility, also calculate center and radius
                const { center, radius } = getCenterAndRadius(draftCameraRegion);
                searchRadius = Math.round(radius);
                searchLocation = {
                    lat: center.latitude,
                    lng: center.longitude,
                };
            }
            else if (searchLocation) {
                // Use circle tracking for non-rectangle searches
                devTool.searchRegion.trackCircle(searchLocation, searchRadius);
            }
            setSearchQuery(prev => (Object.assign(Object.assign({}, prev), { location: searchLocation !== null && searchLocation !== void 0 ? searchLocation : prev.location, radiusMeter: searchRadius !== null && searchRadius !== void 0 ? searchRadius : prev.radiusMeter })));
            // Call different API based on search mode
            if (searchMode === 'toilet') {
                // 카메라 영역 검색('이 지역 재검색')이면 뷰포트 중심/반경으로 제한한다.
                // place 검색과 동일하게 draftCameraRegion이 있으면 rectangleRegion이 세팅되고,
                // searchLocation(중심)/searchRadius(대각 반경)도 함께 계산되어 있다.
                // searchToilets는 currentLocation + distanceMetersLimit(반경)을 받으므로
                // 카메라 영역의 중심을 currentLocation으로, 뷰포트 반경을 distanceMetersLimit으로 넘긴다.
                const isCameraRegionSearch = rectangleRegion != null;
                const toiletCurrentLocation = isCameraRegionSearch
                    ? searchLocation
                    : (location !== null && location !== void 0 ? location : currentLocation);
                if (!toiletCurrentLocation) {
                    ToastUtils.show('현재 위치를 확인할 수 없습니다.');
                    return { mode: 'toilet', items: [] };
                }
                const response = yield toiletApi.searchToilets({
                    currentLocation: toiletCurrentLocation,
                    limit: 50,
                    distanceMetersLimit: isCameraRegionSearch ? searchRadius : null,
                }, { signal });
                const result = (_d = (_c = response === null || response === void 0 ? void 0 : response.data.items) === null || _c === void 0 ? void 0 : _c.map(mapSummaryToToiletDetails)) !== null && _d !== void 0 ? _d : [];
                const requestId = generateRequestId();
                setSearchRequestId(requestId);
                logger.logElementClick('toilet_search', {
                    search_query: text,
                    search_request_id: requestId,
                    search_lat: toiletCurrentLocation === null || toiletCurrentLocation === void 0 ? void 0 : toiletCurrentLocation.lat,
                    search_lng: toiletCurrentLocation === null || toiletCurrentLocation === void 0 ? void 0 : toiletCurrentLocation.lng,
                    search_region_type: isCameraRegionSearch ? 'rectangle' : 'nearest',
                    search_radius: isCameraRegionSearch ? searchRadius : undefined,
                    result_count: result.length,
                    top_result_ids: result
                        .slice(0, 3)
                        .map(item => item.id)
                        .join(','),
                });
                if (result.length === 0) {
                    ToastUtils.show('검색 결과가 없습니다.');
                }
                (_e = onFetchCompleted.current) === null || _e === void 0 ? void 0 : _e.call(onFetchCompleted, result);
                onFetchCompleted.current = () => { };
                return { mode: 'toilet', items: result };
            }
            // Default: place search
            const response = yield api.searchPlacesPost({
                searchText: text,
                distanceMetersLimit: rectangleRegion
                    ? undefined
                    : (radiusMeter !== null && radiusMeter !== void 0 ? radiusMeter : 20000),
                currentLocation: rectangleRegion
                    ? undefined
                    : (location !== null && location !== void 0 ? location : currentLocation),
                rectangleRegion: rectangleRegion,
                sort: sort,
                filters: {
                    maxAccessibilityScore: scoreUnder !== null && scoreUnder !== void 0 ? scoreUnder : undefined,
                    hasSlope: hasSlope !== null && hasSlope !== void 0 ? hasSlope : undefined,
                    isRegistered: isRegistered !== null && isRegistered !== void 0 ? isRegistered : undefined,
                    hasReview: hasReview !== null && hasReview !== void 0 ? hasReview : undefined,
                },
            }, { signal });
            // "<지역명> 화장실" 검색은 place 엔드포인트로 나갔지만 서버가 장애인 화장실 검색으로
            // 처리해 내려줄 수 있다. 이 경우 2차 fetch 없이 인라인 toiletItems를 그대로 화장실 결과로 쓴다.
            if ((response === null || response === void 0 ? void 0 : response.data.mode) === PlaceSearchResultModeDto.AccessibleToilet) {
                const toiletResult = ((_f = response.data.toiletItems) !== null && _f !== void 0 ? _f : []).map(mapSummaryToToiletDetails);
                const requestId = generateRequestId();
                setSearchRequestId(requestId);
                logger.logElementClick('toilet_search', {
                    search_query: text,
                    search_request_id: requestId,
                    search_region_type: 'region_keyword',
                    result_count: toiletResult.length,
                    top_result_ids: toiletResult
                        .slice(0, 3)
                        .map(item => item.id)
                        .join(','),
                });
                if (toiletResult.length === 0) {
                    ToastUtils.show('검색 결과가 없습니다.');
                }
                (_g = onFetchCompleted.current) === null || _g === void 0 ? void 0 : _g.call(onFetchCompleted, toiletResult);
                onFetchCompleted.current = () => { };
                return { mode: 'toilet', items: toiletResult };
            }
            const result = (response === null || response === void 0 ? void 0 : response.data.items) || [];
            const requestId = generateRequestId();
            setSearchRequestId(requestId);
            logger.logElementClick('place_search', {
                search_query: text,
                search_request_id: requestId,
                search_lat: searchLocation === null || searchLocation === void 0 ? void 0 : searchLocation.lat,
                search_lng: searchLocation === null || searchLocation === void 0 ? void 0 : searchLocation.lng,
                search_region_type: rectangleRegion ? 'rectangle' : 'circle',
                search_radius: rectangleRegion ? undefined : searchRadius,
                search_rect_left_top_lat: rectangleRegion === null || rectangleRegion === void 0 ? void 0 : rectangleRegion.leftTopLocation.lat,
                search_rect_left_top_lng: rectangleRegion === null || rectangleRegion === void 0 ? void 0 : rectangleRegion.leftTopLocation.lng,
                search_rect_right_bottom_lat: rectangleRegion === null || rectangleRegion === void 0 ? void 0 : rectangleRegion.rightBottomLocation.lat,
                search_rect_right_bottom_lng: rectangleRegion === null || rectangleRegion === void 0 ? void 0 : rectangleRegion.rightBottomLocation.lng,
                search_sort: sort,
                filter_score_under: scoreUnder,
                filter_has_slope: hasSlope,
                filter_is_registered: isRegistered,
                use_camera_region: useCameraRegion,
                result_count: result.length,
                top_result_ids: result
                    .slice(0, 3)
                    .map(item => item.place.id)
                    .join(','),
            });
            if (result.length === 0) {
                ToastUtils.show('검색 결과가 없습니다.');
            }
            (_h = onFetchCompleted.current) === null || _h === void 0 ? void 0 : _h.call(onFetchCompleted, result);
            onFetchCompleted.current = () => { };
            return { mode: 'place', items: result };
        }),
    });
    const onFetchCompleted = useRef(() => { });
    const cameraFitDeferredUntilKeyboardDismissRef = useRef(null);
    const { updateQuery } = useUpdateSearchQuery();
    const setOnFetchCompleted = callback => {
        cameraFitDeferredUntilKeyboardDismissRef.current = null;
        onFetchCompleted.current = (result) => {
            if (keyboardRef.current.keyboardShown) {
                // 키보드가 올라와 있으면 실행 연기
                cameraFitDeferredUntilKeyboardDismissRef.current = () => callback(result);
            }
            else {
                // 키보드가 내려가 있으면 즉시 실행
                setTimeout(() => callback(result), 100); // 지도 하단의 카드 리스트가 그려지는 것을 기다리기 위해 100ms 기다렸다가 렌더링한다. (100ms는 heuristic)
            }
        };
    };
    // keyboard 상태가 변경될 때마다 ref 업데이트
    useEffect(() => {
        keyboardRef.current = keyboard;
    }, [keyboard]);
    const hasActiveSearch = !!text;
    const isTransitioningToMapView = !viewState.inputMode && viewState.type === 'map';
    // mode/items를 항상 같은 응답에서 가져와 desync를 막는다. 렌더는 resultMode로 분기한다.
    const items = (_b = data === null || data === void 0 ? void 0 : data.items) !== null && _b !== void 0 ? _b : [];
    const resultMode = (_c = data === null || data === void 0 ? void 0 : data.mode) !== null && _c !== void 0 ? _c : 'place';
    useEffect(() => {
        if (isTransitioningToMapView && hasActiveSearch && !isFetching) {
            setTimeout(() => {
                var _a;
                (_a = onFetchCompleted.current) === null || _a === void 0 ? void 0 : _a.call(onFetchCompleted, items);
                onFetchCompleted.current = () => { };
            });
        }
    }, [viewState.inputMode, viewState.type]);
    // 키보드가 내려가면 pending callback 실행
    useEffect(() => {
        if (!keyboard.keyboardShown &&
            cameraFitDeferredUntilKeyboardDismissRef.current) {
            const pendingCallback = cameraFitDeferredUntilKeyboardDismissRef.current;
            setTimeout(pendingCallback, 100); // 지도 하단의 카드 리스트가 그려지는 것을 기다리기 위해 100ms 기다렸다가 렌더링한다. (100ms는 heuristic)
            cameraFitDeferredUntilKeyboardDismissRef.current = null;
        }
    }, [keyboard.keyboardShown]);
    return {
        // 검색어가 없으면 null을 반환해 랜딩(최근 검색어/추천) 화면을 띄운다.
        // items로 뭉개면 []가 되어 "검색 결과 없음"이 대신 렌더된다.
        data: hasActiveSearch ? items : null,
        resultMode,
        isLoading: isFetching,
        refetch,
        updateQuery,
        setOnFetchCompleted,
    };
}
